import { useLocalizationEnabled } from '@user/store/tenantFeatures/hooks/useLocalizationEnabled'
import { format, parseISO } from 'date-fns'

import { selectCurrentUser } from '@user/store/authorization/userSlice'
import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import { TenantLocalDateFormat } from '@sales-center/common'

export function useLocalizationDateFormat() {
  const isLocalizationEnable = useLocalizationEnabled()

  const { userDateFormat } = useSelector(selectCurrentUser)

  const culture = useMemo(() => {
    if (
      isLocalizationEnable &&
      userDateFormat &&
      userDateFormat == TenantLocalDateFormat.CANADA_FORMAT
    ) {
      return 'en-CA'
    }
    if (
      isLocalizationEnable &&
      userDateFormat &&
      userDateFormat == TenantLocalDateFormat.USA_FORMAT
    ) {
      return 'en-US'
    }
    return 'en-US'
  }, [isLocalizationEnable, userDateFormat])

  function formatDate(
    date: string | number | Date | null | undefined,
    type?: string,
  ) {
    if (!date) return ''
    try {
      date = new Date(date)
      const formatter = new Intl.DateTimeFormat(culture, getOptions(type))
      return normalizeDate(formatter.format(date))
    } catch {
      return 'Invalid date'
    }
  }

  function normalizeDate(text: string) {
    return text
      .replace(/-/g, '/')
      .replace(/\b(a\.?m\.?|p\.?m\.?)(?!\w)/gi, (match) => {
        return match[0].toUpperCase() === 'A' ? 'AM' : 'PM'
      })
  }

  function formatDateTime(date: string, timeFormat?: string) {
    return date
      ? format(parseISO(date), `${getFormat()}${timeFormat || ', hh:mm aa'}`)
      : null
  }

  function getFormat() {
    if (isLocalizationEnable && userDateFormat) {
      return userDateFormat
    }
    return 'MM/dd/yyyy'
  }

  function getOptions(type: string | undefined) {
    if (type == 'dateTime' || type == 'dateTime_12') {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h12',
      }
      return options
    }
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }
    return options
  }

  return {
    formatDate,
    getFormat,
    formatDateTime,
    culture,
  }
}
